import React, { useState, useEffect } from "react";
import { api } from "../utils/api";
import { showErrorToast, showSuccessToast } from "../utils/toast";
import ModalPopup from "./ModalPopup";
import { DATA_GRID_STYLES } from "./BasicUserInfo";

const AdminContractManager = ({ subscriptionId, onContractUpdated }) => {
  const [contractData, setContractData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [showRegenerateModal, setShowRegenerateModal] = useState(false);
  const [resetReason, setResetReason] = useState("");
  const [archiveReason, setArchiveReason] = useState("");
  const [regenerateReason, setRegenerateReason] = useState("");

  useEffect(() => {
    if (subscriptionId) {
      fetchContractData();
    }
  }, [subscriptionId]);

  const fetchContractData = async () => {
    try {
      setLoading(true);
      const response = await api(`v1/contract/subscription/${subscriptionId}`, "GET");

      if (response.data.success) {
        setContractData(response.data.data);
      } else {
        console.error("Failed to fetch contract data:", response.data.message);
      }
    } catch (error) {
      console.error("Error fetching contract data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleResetContract = async () => {
    if (!contractData?.contract?.contractId) {
      showErrorToast("No contract found to reset");
      return;
    }

    try {
      setActionLoading(true);
      const response = await api(`v1/contract/admin/reset/${contractData.contract.contractId}`, "PUT", {
        reason: resetReason
      });

      if (response.data.success) {
        showSuccessToast("Contract reset to pending status successfully");
        setShowResetModal(false);
        setResetReason("");
        await fetchContractData();
        if (onContractUpdated) onContractUpdated();
      } else {
        showErrorToast(response.data.message || "Failed to reset contract");
      }
    } catch (error) {
      console.error("Error resetting contract:", error);
      showErrorToast("Failed to reset contract. Please try again.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleArchiveContract = async () => {
    if (!contractData?.contract?.contractId) {
      showErrorToast("No contract found to archive");
      return;
    }

    try {
      setActionLoading(true);
      const response = await api(`v1/contract/admin/archive/${contractData.contract.contractId}`, "PUT", {
        reason: archiveReason
      });

      if (response.data.success) {
        showSuccessToast("Contract archived successfully");
        setShowArchiveModal(false);
        setArchiveReason("");
        await fetchContractData();
        if (onContractUpdated) onContractUpdated();
      } else {
        showErrorToast(response.data.message || "Failed to archive contract");
      }
    } catch (error) {
      console.error("Error archiving contract:", error);
      showErrorToast("Failed to archive contract. Please try again.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleRegenerateContract = async () => {
    try {
      setActionLoading(true);
      const response = await api(`v1/contract/admin/regenerate/${subscriptionId}`, "POST", {
        reason: regenerateReason
      });

      if (response.data.success) {
        showSuccessToast("Contract regenerated successfully");
        setShowRegenerateModal(false);
        setRegenerateReason("");
        await fetchContractData();
        if (onContractUpdated) onContractUpdated();
      } else {
        showErrorToast(response.data.message || "Failed to regenerate contract");
      }
    } catch (error) {
      console.error("Error regenerating contract:", error);
      showErrorToast("Failed to regenerate contract. Please try again.");
    } finally {
      setActionLoading(false);
    }
  };

  const downloadContract = async () => {
    if (!contractData?.contract?.contractId) {
      showErrorToast("No contract found to download");
      return;
    }

    try {
      const response = await api(`v1/contract/admin/download/${contractData.contract.contractId}`, "GET");

      if (response.data.success && response.data.data.downloadUrl) {
        // Open the download URL in a new tab
        window.open(response.data.data.downloadUrl, "_blank");
      } else {
        showErrorToast("Failed to generate download link");
      }
    } catch (error) {
      console.error("Error downloading contract:", error);
      showErrorToast("Failed to download contract. Please try again.");
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "accepted":
        return "bg-green-600 text-white";
      case "pending":
        return "bg-yellow-600 text-white";
      case "rejected":
        return "bg-red-600 text-white";
      case "archived":
        return "bg-gray-600 text-white";
      default:
        return "bg-gray-600 text-white";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-AU", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-1/3"></div>
        </div>
      </div>
    );
  }

  if (!contractData?.requiresContract) {
    return (
      <div className={DATA_GRID_STYLES}>
        <span>Contract Required</span>
        <span>No</span>
        <span>Status</span>
        <span>Not Required</span>
      </div>
    );
  }

  const contract = contractData.contract;

  return (
    <div>
      {contract ? (
        <div>
          {/* Contract Details */}
          <div className={DATA_GRID_STYLES}>
            <span>Contract ID</span>
            <span>{contract.contractId}</span>
            <span>Status</span>
            <span>
              <span
                className={`inline-block px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md ${getStatusColor(
                  contract.status
                )}`}
              >
                {contract.status?.toUpperCase() || "UNKNOWN"}
              </span>
            </span>
            <span>Created</span>
            <span>{formatDate(contract.createdAt)}</span>
            <span>Signed Date</span>
            <span>{formatDate(contract.signedDate)}</span>
            <span>IP Address</span>
            <span>{contract.ipAddress || "N/A"}</span>
            <span>Has Signature</span>
            <span>{contract.hasSignature ? "Yes" : "No"}</span>
            <span>Download</span>
            <span>
              {contract.canDownload ? (
                <button
                  onClick={downloadContract}
                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  PDF
                </button>
              ) : (
                "N/A"
              )}
            </span>
            <span>Admin Actions</span>
            <span>
              <div className="flex flex-wrap gap-2">
                {contract.status === "accepted" && (
                  <>
                    <button
                      onClick={() => setShowResetModal(true)}
                      className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-yellow-600 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
                    >
                      Reset
                    </button>
                    <button
                      onClick={() => setShowArchiveModal(true)}
                      className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-gray-600 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                    >
                      Archive
                    </button>
                  </>
                )}
                <button
                  onClick={() => setShowRegenerateModal(true)}
                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Regenerate
                </button>
              </div>
            </span>
          </div>
        </div>
      ) : (
        <div className={DATA_GRID_STYLES}>
          <span>Contract Status</span>
          <span>Not Found</span>
          <span>Action</span>
          <span>
            <button
              onClick={() => setShowRegenerateModal(true)}
              className="inline-flex items-center px-2 py-1 border border-transparent text-xs leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Generate
            </button>
          </span>
        </div>
      )}

      {/* Reset Contract Modal */}
      <ModalPopup
        showModal={showResetModal}
        handleShowModal={() => setShowResetModal(false)}
        popupTitle="Reset Contract to Pending"
        popupContent={
          <div className="p-6">
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-4">
                This will reset the contract to pending status, requiring the customer to sign it again.
              </p>
              <label className="block text-sm font-medium text-gray-700 mb-2">Reason for reset:</label>
              <textarea
                value={resetReason}
                onChange={(e) => setResetReason(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={3}
                placeholder="Enter reason for resetting the contract..."
              />
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowResetModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Cancel
              </button>
              <button
                onClick={handleResetContract}
                disabled={actionLoading}
                className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-yellow-600 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {actionLoading ? "Resetting..." : "Reset Contract"}
              </button>
            </div>
          </div>
        }
      />

      {/* Archive Contract Modal */}
      <ModalPopup
        showModal={showArchiveModal}
        handleShowModal={() => setShowArchiveModal(false)}
        popupTitle="Archive Contract"
        popupContent={
          <div className="p-6">
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-4">
                This will archive the contract, marking it as no longer active.
              </p>
              <label className="block text-sm font-medium text-gray-700 mb-2">Reason for archiving:</label>
              <textarea
                value={archiveReason}
                onChange={(e) => setArchiveReason(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={3}
                placeholder="Enter reason for archiving the contract..."
              />
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowArchiveModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Cancel
              </button>
              <button
                onClick={handleArchiveContract}
                disabled={actionLoading}
                className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-gray-600 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {actionLoading ? "Archiving..." : "Archive Contract"}
              </button>
            </div>
          </div>
        }
      />

      {/* Regenerate Contract Modal */}
      <ModalPopup
        showModal={showRegenerateModal}
        handleShowModal={() => setShowRegenerateModal(false)}
        popupTitle="Regenerate Contract"
        popupContent={
          <div className="p-6">
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-4">
                This will create a new contract for the customer to sign. Any existing contract will be archived.
              </p>
              <label className="block text-sm font-medium text-gray-700 mb-2">Reason for regeneration:</label>
              <textarea
                value={regenerateReason}
                onChange={(e) => setRegenerateReason(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={3}
                placeholder="Enter reason for regenerating the contract..."
              />
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowRegenerateModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Cancel
              </button>
              <button
                onClick={handleRegenerateContract}
                disabled={actionLoading}
                className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {actionLoading ? "Regenerating..." : "Regenerate Contract"}
              </button>
            </div>
          </div>
        }
      />
    </div>
  );
};

export default AdminContractManager;
